import { resolveApiBase } from './api'

export interface ClientTelemetryEvent {
  eventType: string
  panel: string
  severity?: 'info' | 'warning' | 'error'
  appName?: string
  message?: string
  details?: Record<string, unknown>
}

const RECENT_EVENT_TTL_MS = 5000
const recentEvents = new Map<string, number>()

function buildEventKey(event: ClientTelemetryEvent): string {
  return [
    event.eventType,
    event.panel,
    event.appName || '',
    event.message || '',
    JSON.stringify(event.details || {}),
  ].join('|')
}

function shouldSkipEvent(event: ClientTelemetryEvent): boolean {
  const now = Date.now()
  const key = buildEventKey(event)
  const lastSentAt = recentEvents.get(key)
  if (lastSentAt && now - lastSentAt < RECENT_EVENT_TTL_MS) {
    return true
  }
  recentEvents.set(key, now)
  for (const [entryKey, sentAt] of recentEvents.entries()) {
    if (now - sentAt >= RECENT_EVENT_TTL_MS) {
      recentEvents.delete(entryKey)
    }
  }
  return false
}

export function recordClientTelemetry(event: ClientTelemetryEvent): void {
  if (typeof window === 'undefined' || shouldSkipEvent(event)) {
    return
  }

  const payload = JSON.stringify({
    event_type: event.eventType,
    panel: event.panel,
    severity: event.severity || 'error',
    app_name: event.appName,
    message: event.message,
    path: window.location.pathname,
    details: event.details || {},
  })
  const url = `${resolveApiBase(window.location.pathname)}/client-observability/events`
  const blob = new Blob([payload], { type: 'application/json' })

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const sent = navigator.sendBeacon(url, blob)
      if (sent) return
    }
  } catch {
    // Fall back to fetch below.
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Telemetry must never break the UI.
  })
}
